// src/utils/frequencyUtils.ts

// Define frequency types and their multipliers
export const PERIODS = [
  'Monthly', 
  'Quarterly', 
  'All Year Round', 
  'Single Date', 
  'Date Range', 
  'Bi-Monthly', 
  'Multiple Dates'
];

/**
 * Returns the appropriate multiplier based on frequency
 * @param frequency The frequency string
 * @returns A multiplier: 12 for monthly, 4 for quarterly, etc.
 */
export const getFrequencyMultiplier = (frequency: string | undefined): number => {
  if (!frequency) return 1;

  switch (frequency) {
    case 'Monthly':
      return 12;
    case 'Quarterly':
      return 4;
    case 'Bi-Monthly':
      return 6;
    case 'All Year Round':
    case 'Single Date':
    case 'Date Range':
    case 'Multiple Dates':
    default:
      return 1;
  }
};

/**
 * Calculates the annualized value based on frequency
 * @param value The base value
 * @param frequency The frequency string
 * @returns The annualized value
 */
export const getAnnualizedValue = (value: number, frequency: string | undefined): number => {
  return value * getFrequencyMultiplier(frequency);
};

/**
 * Determines if a value needs to be displayed with its frequency
 * @param frequency The frequency string
 * @returns Boolean indicating if frequency should be displayed
 */
export const shouldShowFrequency = (frequency: string | undefined): boolean => {
  if (!frequency) return false;
  return ['Monthly', 'Quarterly', 'Bi-Monthly'].includes(frequency);
};

/**
 * Formats a value with its frequency for display
 * @param value The value to format
 * @param frequency The frequency string
 * @param options Formatting options
 * @returns Formatted string with value and frequency if needed
 */
export const formatWithFrequency = (
  value: number, 
  frequency: string | undefined, 
  options: { showAnnual?: boolean; showSymbol?: string } = {}
): string => {
  const { showAnnual = true, showSymbol = '€' } = options;
  
  if (!shouldShowFrequency(frequency) || !showAnnual) {
    return `${showSymbol}${value.toLocaleString()}`;
  }
  
  let periodLabel = '';
  switch (frequency) {
    case 'Monthly':
      periodLabel = '/month';
      break;
    case 'Quarterly':
      periodLabel = '/quarter';
      break;
    case 'Bi-Monthly':
      periodLabel = '/bi-month';
      break;
    default:
      periodLabel = '';
  }
  
  const annual = getAnnualizedValue(value, frequency);
  return `${showSymbol}${value.toLocaleString()}${periodLabel} (${showSymbol}${annual.toLocaleString()}/year)`;
};